import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { Button, buttonVariants } from "@/components/ui/button";
import type { SnipeItLocation, SnipeItModel, SnipeItCategory } from "@/lib/snipeit/types";
import { useLocationAssetsPage } from "@/common/api/use-location-assets-page";
import { useMoveAssets } from "@/common/api/use-move-assets";
import { useLocationDetail } from "@/common/api/use-location-detail";
import { Pagination } from "@/components/pagination";
import { LocationPicker } from "./location-picker";
import { AssetFilterBar } from "./asset-filter-bar";
import { AssetTable } from "./asset-table";
import { Link } from "wouter";

type SortKey = "asset_tag" | "model" | "serial" | "category";
type SortDir = "asc" | "desc";

const PAGE_SIZE = 50;

export function LocationChangeView() {
  const { location: savedLocation } = useLocationDetail();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [sortKey, setSortKey] = useState<SortKey>("asset_tag");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(0);

  const [modelFilter, setModelFilter] = useState<SnipeItModel | null>(null);
  const [modelInput, setModelInput] = useState("");

  const [categoryFilter, setCategoryFilter] = useState<SnipeItCategory | null>(null);
  const [categoryInput, setCategoryInput] = useState("");

  const [targetLocation, setTargetLocation] = useState<SnipeItLocation | null>(null);
  const [targetInput, setTargetInput] = useState("");

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [movingIds, setMovingIds] = useState<Set<number>>(new Set());
  const [movedIds, setMovedIds] = useState<Set<number>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<number>>(new Set());

  const sourceLocation = savedLocation;

  const { data, isLoading, isFetching } = useLocationAssetsPage({
    locationId: sourceLocation?.id,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    sort: sortKey,
    order: sortDir,
    search: debouncedSearch || undefined,
    modelId: modelFilter?.id,
    categoryId: categoryFilter?.id,
  });

  const assets = useMemo(() => data?.rows ?? [], [data?.rows]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  }

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const availableIds = assets.filter((a) => !movedIds.has(a.id)).map((a) => a.id);
    const allSelected = availableIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of availableIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of availableIds) next.add(id);
        return next;
      });
    }
  }, [assets, selectedIds, movedIds]);

  const moveMutation = useMoveAssets({
    onMutate: (ids) => setMovingIds(new Set(ids)),
    onSuccess: ({ fulfilled, rejected }) => {
      setMovedIds((prev) => {
        const next = new Set(prev);
        for (const id of fulfilled) next.add(id);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of fulfilled) next.delete(id);
        return next;
      });
      setErrorIds((prev) => {
        const next = new Set(prev);
        for (const id of rejected) next.add(id);
        return next;
      });
    },
    onSettled: () => setMovingIds(new Set()),
  });

  const canMove = targetLocation && selectedIds.size > 0 && !moveMutation.isPending;
  const isMoving = moveMutation.isPending;

  return (
    <main className="bg-background flex min-h-dvh flex-col overflow-y-auto px-4 pt-6 md:h-dvh md:overflow-hidden md:px-6">
      <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col gap-4">
        {!sourceLocation && (
          <div className="border-border bg-card rounded-xl border p-6 text-center shadow-sm">
            <p className="text-muted-foreground text-sm">
              Bitte zuerst einen Standort in den{" "}
              <Link href="/settings" className="text-primary underline">
                Einstellungen
              </Link>{" "}
              festlegen.
            </p>
          </div>
        )}

        {sourceLocation && (
          <>
            <LocationPicker
              sourceLocation={sourceLocation}
              targetInput={targetInput}
              onTargetInputChange={(value) => {
                setTargetInput(value);
                if (targetLocation && value !== targetLocation.name) {
                  setTargetLocation(null);
                }
              }}
              onTargetSelect={(loc) => {
                setTargetLocation(loc);
                setTargetInput(loc.name);
              }}
            />

            <AssetFilterBar
              search={search}
              onSearchChange={(value) => {
                setSearch(value);
                setPage(0);
              }}
              modelFilter={modelFilter}
              modelInput={modelInput}
              onModelInputChange={(value) => {
                setModelInput(value);
                if (modelFilter && value !== modelFilter.name) {
                  setModelFilter(null);
                  setPage(0);
                }
              }}
              onModelSelect={(m) => {
                setModelFilter(m);
                setModelInput(m.name);
                setPage(0);
              }}
              onModelClear={() => {
                setModelFilter(null);
                setModelInput("");
                setPage(0);
              }}
              categoryFilter={categoryFilter}
              categoryInput={categoryInput}
              onCategoryInputChange={(value) => {
                setCategoryInput(value);
                if (categoryFilter && value !== categoryFilter.name) {
                  setCategoryFilter(null);
                  setPage(0);
                }
              }}
              onCategorySelect={(c) => {
                setCategoryFilter(c);
                setCategoryInput(c.name);
                setPage(0);
              }}
              onCategoryClear={() => {
                setCategoryFilter(null);
                setCategoryInput("");
                setPage(0);
              }}
            />

            <AssetTable
              assets={assets}
              isLoading={isLoading}
              sortKey={sortKey}
              sortDir={sortDir}
              onToggleSort={toggleSort}
              selectedIds={selectedIds}
              movingIds={movingIds}
              movedIds={movedIds}
              errorIds={errorIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
            />

            <Pagination page={page} totalPages={totalPages} isFetching={isFetching} onPageChange={setPage} />

            <div className="flex flex-col gap-3 pb-4 md:flex-row md:items-center md:justify-between">
              <Link href="/" className={buttonVariants({ variant: "secondary", size: "sm" })}>
                Zurück
              </Link>
              <div className="flex w-full flex-wrap items-center gap-3 md:w-auto md:justify-end">
                {selectedIds.size > 0 && (
                  <span className="text-muted-foreground text-xs">
                    {selectedIds.size} {selectedIds.size === 1 ? "Gerät" : "Geräte"} ausgewählt
                  </span>
                )}
                {movedIds.size > 0 && <span className="text-xs text-green-600">{movedIds.size} verschoben</span>}
                <Button
                  size="sm"
                  disabled={!canMove}
                  onClick={() => {
                    if (!targetLocation) return;
                    moveMutation.mutate({ ids: [...selectedIds], targetId: targetLocation.id });
                  }}
                >
                  {isMoving ? "Wird verschoben…" : "Standort wechseln"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
